import { Injectable } from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {AuthorityData} from "../model/authorityData";
import {SubauthorityData} from "../model/subauthorityData";
import {Property} from "../model/property";
import {NgxIndexedDBService} from "ngx-indexed-db";
import * as Sentry from "@sentry/angular";

@Injectable({
  providedIn: 'root'
})
export class AuthorityService {

  constructor(private httpClient: HttpClient, private indexDBService: NgxIndexedDBService  ) {
  }

  private lastPropertyHash: string = "";

  setLastPropertyHash(property: Property){
    if (!property){
      this.lastPropertyHash = "";
    }else{

      this.lastPropertyHash = this.getPropertyHash(property);
    }
  }

  getLastPropertyHash(){
    return this.lastPropertyHash;
  }

  /**
   * Deletes multiple entries from the 'authorities' store in the IndexedDB for a specific property by creating a property hash.
   *
   * @param property Property for which all 'authorities' should be removed from the indexedDb. Used to create the property hash
   */
  public async removeAuthoritiesFromIndexedDbByProperty(property: Property): Promise<void> {
    try {
      const propertyHash = this.getPropertyHash(property);
      let entries = await this.getEntriesByPropertyHash(propertyHash);
      const deletePromises = entries.map(entry => this.deleteEntryByKey(entry.authorityId));
      await Promise.all(deletePromises);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error deleting entries:', error);
    }
  }


  saveFile(file: File, propertyHash: string, type: string): void {
    const record = {
      file: file,
      propertyHash: propertyHash,
      type: type
    };

    this.indexDBService.add('authorities', record).subscribe();
  }


  httpOptions = {
    withCredentials: true //this is required so that Angular returns the Cookies received from the server. The server sends cookies in Set-Cookie header. Without this, Angular will ignore the Set-Cookie header
  };

  setAuthority(authorityData: AuthorityData): Observable<any> {
    let httpOptions = {
      withCredentials: true,
      responseType: 'text' as 'json'
    }
    return this.httpClient.post("/api/authority-data",authorityData, httpOptions);
  }

  setSubAuthority(authorityData: SubauthorityData): Observable<any> {
    let httpOptions = {
      withCredentials: true,
      responseType: 'text' as 'json'
    }
    return this.httpClient.post("/api/sub-authority-data",authorityData, httpOptions);
  }

  /**
   * Generates and returns a property hash which is build from the property street, zip, town and house_number
   *
   * @param property Property for which the property hash should be generated
   * @return string representing the generated property hash
   */
  private getPropertyHash(property: Property): string {
    return `${property.street}_${property.zip}_${property.town}_${property.house_number || ''}`;
  }

  /**
   * Deletes an entry from the 'authorities' store in IndexedDB based on its key.
   * @param {number} key - The key of the entry to delete.
   * @returns {Promise<void>} A promise that resolves when the entry has been deleted, or rejects with an error.
   */
  private async deleteEntryByKey(key: number): Promise<void> {
    try {
      await this.indexDBService.delete('authorities', key).toPromise();
    } catch (error) {
      Sentry.captureException(error);
      console.error(`Error deleting entry with key ${key}:`, error);
    }
  }

  /**
   * Retrieves all entries from the 'authorities' store in IndexedDB that match a specific property hash.
   * @param {string} propertyHash - The hash of the property to search for in the 'propertyHash' index.
   * @returns {Promise<any[]>} A promise that resolves to an array of entries matching the specified property hash.
   */
  private getEntriesByPropertyHash(propertyHash: string): Promise<any[]> {
    return this.indexDBService.getAllByIndex('authorities', 'propertyHash', IDBKeyRange.only(propertyHash)).toPromise();
  }

}
